import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRight, Megaphone, Bot, Server, FileText, CreditCard, TrendingUp, DollarSign, Share2, MessageSquare, Mail, Receipt, FileSignature, Plus, Clock, CheckCircle, X, Loader2, Paperclip, FileUp, FolderOpen, Building2, Package, Landmark } from 'lucide-react';
import { Button } from '../components/ui/button';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import Breadcrumb from '../components/Breadcrumb';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Payment categories
const PAYMENT_CATEGORIES = [
  { id: 'marketing', name: 'مدفوعات التسويق', icon: Megaphone },
  { id: 'ai', name: 'مدفوعات الذكاء الاصطناعي', icon: Bot },
  { id: 'servers', name: 'مدفوعات الخوادم', icon: Server },
  { id: 'social-media', name: 'مدفوعات برامج التواصل الاجتماعي', icon: Share2 },
  { id: 'sms', name: 'مدفوعات الرسائل النصية', icon: MessageSquare },
  { id: 'email', name: 'مدفوعات البريد الإلكتروني', icon: Mail },
];

export default function Finance() {
  const [activeSection, setActiveSection] = useState('main');
  const [activeCompany, setActiveCompany] = useState(null);
  const navigate = useNavigate();
  const { token } = useAuth();
  const { language } = useLanguage();

  // Data states
  const [invoices, setInvoices] = useState([]);
  const [contracts, setContracts] = useState([]);
  const [categoryRecords, setCategoryRecords] = useState([]);
  const [invoiceTitles, setInvoiceTitles] = useState([]);
  const [pendingCount, setPendingCount] = useState({ invoices: 0, contracts: 0 });
  const [loading, setLoading] = useState(false);

  const getHeaders = () => {
    if (!token) return {};
    return { Authorization: `Bearer ${token}` };
  };

  // Fetch invoice titles
  useEffect(() => {
    const fetchTitles = async () => {
      if (!token) return;
      try {
        const res = await axios.get(`${BACKEND_URL}/api/finance/invoice-titles`, { headers: getHeaders() });
        setInvoiceTitles(res.data.data || []);
      } catch (err) {
        console.log('Error fetching titles:', err);
      }
    };
    fetchTitles();
  }, [token]);

  // Fetch pending counts
  useEffect(() => {
    const fetchPendingCount = async () => {
      if (!token) return;
      try {
        const res = await axios.get(`${BACKEND_URL}/api/finance/pending-count`, { headers: getHeaders() });
        setPendingCount(res.data);
      } catch (err) {
        console.log('Error fetching pending count:', err);
      }
    };
    fetchPendingCount();
  }, [token]);

  // Fetch invoices when in invoice section
  useEffect(() => {
    const fetchInvoices = async () => {
      if (activeSection !== 'add-invoice' || !token) return;
      setLoading(true);
      try {
        const res = await axios.get(`${BACKEND_URL}/api/finance/invoices`, { headers: getHeaders() });
        setInvoices(res.data.data || []);
      } catch (err) {
        console.log('Error fetching invoices:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchInvoices();
  }, [activeSection, token]);

  // Fetch contracts when in contract section
  useEffect(() => {
    const fetchContracts = async () => {
      if (activeSection !== 'add-contract' || !token) return;
      setLoading(true);
      try {
        const res = await axios.get(`${BACKEND_URL}/api/finance/contracts`, { headers: getHeaders() });
        setContracts(res.data.data || []);
      } catch (err) {
        console.log('Error fetching contracts:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchContracts();
  }, [activeSection, token]);

  // Fetch category records when viewing a payment category
  useEffect(() => {
    const fetchCategoryRecords = async () => {
      const category = PAYMENT_CATEGORIES.find(c => c.id === activeSection);
      if (!category || !token) return;
      setLoading(true);
      try {
        const res = await axios.get(`${BACKEND_URL}/api/finance/records/${activeSection}`, { headers: getHeaders() });
        setCategoryRecords(res.data.data || []);
      } catch (err) {
        console.log('Error fetching category records:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchCategoryRecords();
  }, [activeSection, token]);

  const companies = [
    {
      id: 'cloudgm',
      name: 'CloudGM',
      nameAr: 'منصة العملاء',
      logo: '/brand/ast-logo-cloudgm.png',
    },
    {
      id: 'cloudgm_business',
      name: 'CloudGM Business',
      nameAr: 'منصة التجار',
      logo: '/brand/ast-logo-cloudgm-business.png',
    },
  ];

  const companySubSections = [
    { id: 'payments', name: 'سجلات المدفوعات', icon: <CreditCard className="h-8 w-8 text-[#c9a227]" /> },
    { id: 'subscriptions', name: 'سجلات الاشتراك', icon: <FileText className="h-8 w-8 text-[#c9a227]" /> },
  ];

  // ─── iter-211: add-invoice/contract form migrated to /dashboard/finance/records/new
  // The following helpers stay for existing-record installment management only.

  // Upload installment attachment for existing record
  const uploadInstallmentAttachment = async (recordId, installmentIndex, file, record) => {
    try {
      const formDataUpload = new FormData();
      formDataUpload.append('file', file);
      
      const res = await axios.post(`${BACKEND_URL}/api/upload/file`, formDataUpload, {
        headers: {
          ...getHeaders(),
          'Content-Type': 'multipart/form-data'
        }
      });
      
      // Update installment with attachment
      await axios.put(
        `${BACKEND_URL}/api/finance/records/${recordId}/installment/${installmentIndex}`,
        { 
          is_paid: false, 
          attachment: res.data.file_id,
          attachment_only: true
        },
        { headers: getHeaders() }
      );
      
      toast.success('تم رفع المرفق بنجاح');
      
      // Refresh data
      if (record.type === 'invoice') {
        const resData = await axios.get(`${BACKEND_URL}/api/finance/invoices`, { headers: getHeaders() });
        setInvoices(resData.data.data || []);
      } else {
        const resData = await axios.get(`${BACKEND_URL}/api/finance/contracts`, { headers: getHeaders() });
        setContracts(resData.data.data || []);
      }
    } catch (err) {
      toast.error('فشل رفع المرفق');
      console.error(err);
    }
  };

  // Mark installment as paid
  const markInstallmentPaid = async (recordId, installmentIndex, record, isLastInstallment) => {
    // Check if attachment exists
    const installment = record.installments[installmentIndex];
    if (!installment.attachment) {
      toast.error('يجب إرفاق ملف قبل تأكيد الدفع');
      return;
    }
    
    // If last installment, show confirmation
    if (isLastInstallment) {
      const confirmed = window.confirm('هذه آخر دفعة. سيتم ترحيل الفاتورة/العقد إلى القسم المالي للمراجعة والتدقيق. هل تريد المتابعة؟');
      if (!confirmed) return;
    }
    
    try {
      await axios.put(
        `${BACKEND_URL}/api/finance/records/${recordId}/installment/${installmentIndex}`,
        { is_paid: true, paid_date: new Date().toISOString() },
        { headers: getHeaders() }
      );
      
      if (isLastInstallment) {
        toast.success('تم تسجيل الدفعة وترحيل السجل للمراجعة');
      } else {
        toast.success('تم تسجيل الدفعة بنجاح');
      }

      // Refresh data
      if (record.type === 'invoice') {
        const res = await axios.get(`${BACKEND_URL}/api/finance/invoices`, { headers: getHeaders() });
        setInvoices(res.data.data || []);
      } else {
        const res = await axios.get(`${BACKEND_URL}/api/finance/contracts`, { headers: getHeaders() });
        setContracts(res.data.data || []);
      }

      // Refresh pending count
      const countRes = await axios.get(`${BACKEND_URL}/api/finance/pending-count`, { headers: getHeaders() });
      setPendingCount(countRes.data);

    } catch (err) {
      toast.error('حدث خطأ');
    }
  };

  // Render records list
  const renderRecordsList = (records, type) => {
    const pendingRecords = records.filter(r => r.status === 'pending');
    const completedRecords = records.filter(r => r.status === 'completed');

    return (
      <div className="space-y-6">
        {/* Add Button — navigates to the new full-page form (iter-211) */}
        <Button
          onClick={() => navigate(`/dashboard/finance/records/new?type=${type}`)}
          className="w-full h-16 bg-gradient-to-r from-emerald-600 to-emerald-700 hover:from-emerald-700 hover:to-emerald-800 text-white rounded-2xl flex items-center justify-center gap-3"
          data-testid={`btn-open-add-${type}`}
        >
          <Plus className="h-6 w-6" />
          <span className="font-bold text-lg">
            {type === 'invoice' ? 'إدخال فاتورة جديدة' : 'إدخال عقد جديد'}
          </span>
        </Button>

        {/* Pending Records */}
        {pendingRecords.length > 0 && (
          <div>
            <h3 className="text-lg font-bold text-amber-400 mb-4 flex items-center gap-2">
              <Clock className="h-5 w-5" />
              {type === 'invoice' ? 'الفواتير المعلقة' : 'العقود المعلقة'}
              <span className="bg-amber-500/20 text-amber-400 px-2 py-0.5 rounded-full text-sm">
                {pendingRecords.length}
              </span>
            </h3>
            <div className="space-y-3">
              {pendingRecords.map((record) => (
                <div key={record.id} className="bg-[#0a0a0a] rounded-2xl border border-amber-500/30 p-4">
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <p className="text-white font-bold">{record.title || 'بدون عنوان'}</p>
                      <p className="text-sm text-[#c9a227]">{PAYMENT_CATEGORIES.find(c => c.id === record.category)?.name || record.category}</p>
                      <p className="text-xs text-[#808080]">{record.description || ''}</p>
                    </div>
                    <div className="text-left">
                      <p className="text-amber-400 font-bold">{record.amount?.toLocaleString()} ر.س</p>
                      <p className="text-xs text-[#606060]">{new Date(record.date).toLocaleDateString('ar-SA')}</p>
                    </div>
                  </div>
                  
                  {/* Installments */}
                  {record.installments && record.installments.length > 0 && (
                    <div className="mt-4 pt-4 border-t border-[#2a2a4e]">
                      <p className="text-sm text-[#808080] mb-3">الدفعات:</p>
                      <div className="grid grid-cols-1 gap-3">
                        {record.installments.map((inst, idx) => {
                          const isLastInstallment = idx === record.installments.length - 1 && 
                            record.installments.slice(0, idx).every(i => i.is_paid);
                          const allPreviousPaid = record.installments.slice(0, idx).every(i => i.is_paid);
                          const canPay = !inst.is_paid && (idx === 0 || allPreviousPaid);
                          
                          return (
                            <div
                              key={idx}
                              className={`p-4 rounded-xl border ${inst.is_paid ? 'bg-emerald-500/10 border-emerald-500/30' : 'bg-[#1a1a2e] border-[#2a2a4e]'}`}
                            >
                              <div className="flex items-center justify-between">
                                <div className="flex-1">
                                  <p className="text-white font-medium">دفعة {idx + 1}</p>
                                  <p className="text-sm text-[#808080]">{inst.amount?.toLocaleString()} ر.س</p>
                                  <p className="text-xs text-[#606060]">{inst.date ? new Date(inst.date).toLocaleDateString('ar-SA') : '-'}</p>
                                  {inst.attachment && (
                                    <p className="text-xs text-emerald-400 mt-1 flex items-center gap-1">
                                      <Paperclip className="h-3 w-3" />
                                      مرفق موجود
                                    </p>
                                  )}
                                </div>
                                <div className="flex items-center gap-2">
                                  {inst.is_paid ? (
                                    <CheckCircle className="h-6 w-6 text-emerald-500" />
                                  ) : canPay && (
                                    <>
                                      {/* Attachment Button */}
                                      <div>
                                        <input
                                          type="file"
                                          className="hidden"
                                          accept=".pdf,.jpg,.jpeg,.png"
                                          onChange={(e) => {
                                            if (e.target.files[0]) {
                                              uploadInstallmentAttachment(record.id, idx, e.target.files[0], record);
                                            }
                                          }}
                                          id={`installment-file-${record.id}-${idx}`}
                                        />
                                        <Button
                                          size="sm"
                                          variant="outline"
                                          onClick={() => document.getElementById(`installment-file-${record.id}-${idx}`).click()}
                                          className={`border-[#2a2a4e] ${inst.attachment ? 'text-emerald-400 border-emerald-500/30' : 'text-white'}`}
                                        >
                                          <Paperclip className="h-4 w-4 me-1" />
                                          {inst.attachment ? 'تم الإرفاق' : 'مرفق'}
                                        </Button>
                                      </div>
                                      
                                      {/* Payment Button - Only enabled if attachment exists */}
                                      <Button
                                        size="sm"
                                        onClick={() => markInstallmentPaid(record.id, idx, record, isLastInstallment)}
                                        disabled={!inst.attachment}
                                        className={`${inst.attachment ? 'bg-emerald-600 hover:bg-emerald-700' : 'bg-gray-600 cursor-not-allowed'} text-white`}
                                      >
                                        تم الدفع
                                      </Button>
                                    </>
                                  )}
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Completed Records - Show only those without installments */}
        {completedRecords.filter(r => !r.installments || r.installments.length === 0).length > 0 && (
          <div>
            <h3 className="text-lg font-bold text-emerald-400 mb-4 flex items-center gap-2">
              <CheckCircle className="h-5 w-5" />
              {type === 'invoice' ? 'فواتير مكتملة (بدون دفعات)' : 'عقود مكتملة (بدون دفعات)'}
            </h3>
            <div className="space-y-3">
              {completedRecords.filter(r => !r.installments || r.installments.length === 0).map((record) => (
                <div key={record.id} className="bg-[#0a0a0a] rounded-2xl border border-emerald-500/30 p-4">
                  <div className="flex items-start justify-between">
                    <div>
                      <p className="text-white font-bold">{record.title || 'بدون عنوان'}</p>
                      <p className="text-sm text-[#c9a227]">{PAYMENT_CATEGORIES.find(c => c.id === record.category)?.name || record.category}</p>
                      <p className="text-xs text-[#606060]">تم الترحيل للأرشيف</p>
                    </div>
                    <div className="text-left">
                      <p className="text-emerald-400 font-bold">{record.amount?.toLocaleString()} ر.س</p>
                      <p className="text-xs text-[#606060]">{new Date(record.date).toLocaleDateString('ar-SA')}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {records.length === 0 && !loading && (
          <div className="text-center py-12 bg-[#0a0a0a] rounded-2xl border border-[#2a2a4e]">
            {type === 'invoice' ? (
              <Receipt className="h-16 w-16 mx-auto text-emerald-500/30 mb-4" />
            ) : (
              <FileSignature className="h-16 w-16 mx-auto text-blue-500/30 mb-4" />
            )}
            <p className="text-[#808080] text-lg mb-2">
              {type === 'invoice' ? 'لا توجد فواتير' : 'لا توجد عقود'}
            </p>
            <p className="text-[#606060] text-sm">اضغط على الزر أعلاه لإضافة سجل جديد</p>
          </div>
        )}
      </div>
    );
  };

  // Render category archive
  const renderCategoryArchive = () => {
    const category = PAYMENT_CATEGORIES.find(c => c.id === activeSection);
    if (!category) return null;
    const CategoryIcon = category.icon;

    return (
      <div className="bg-[#1a1a2e] rounded-3xl border border-[#2a2a4e] p-6">
        <div className="flex items-center gap-3 mb-6">
          <CategoryIcon className="h-8 w-8 text-[#c9a227]" />
          <h2 className="text-xl font-bold text-white">{category.name}</h2>
        </div>

        {loading ? (
          <div className="text-center py-16">
            <Loader2 className="h-8 w-8 mx-auto text-[#c9a227] animate-spin" />
          </div>
        ) : categoryRecords.length === 0 ? (
          <div className="text-center py-16 bg-[#0a0a0a] rounded-2xl border border-[#2a2a4e]">
            <DollarSign className="h-16 w-16 mx-auto text-[#c9a227]/30 mb-4" />
            <p className="text-[#808080] text-lg mb-2">لا توجد سجلات مكتملة</p>
            <p className="text-[#606060] text-sm">ستظهر هنا الفواتير والعقود المكتملة في هذه الفئة</p>
          </div>
        ) : (
          <div className="space-y-3">
            {categoryRecords.map((record) => (
              <div key={record.id} className="bg-[#0a0a0a] rounded-2xl border border-[#2a2a4e] p-4">
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-3">
                    {record.type === 'invoice' ? (
                      <Receipt className="h-5 w-5 text-emerald-500" />
                    ) : (
                      <FileSignature className="h-5 w-5 text-blue-500" />
                    )}
                    <div>
                      <p className="text-white font-bold">{record.title || (record.type === 'invoice' ? 'فاتورة' : 'عقد')}</p>
                      <p className="text-sm text-[#808080]">{record.description || ''}</p>
                      {record.attachment && (
                        <p className="text-xs text-emerald-400 flex items-center gap-1 mt-1">
                          <Paperclip className="h-3 w-3" />
                          يوجد مرفق
                        </p>
                      )}
                    </div>
                  </div>
                  <div className="text-left">
                    <p className="text-[#c9a227] font-bold">{record.amount?.toLocaleString()} ر.س</p>
                    <p className="text-xs text-[#606060]">{new Date(record.date).toLocaleDateString('ar-SA')}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  const renderCompanyContent = () => {
    const company = companies.find(c => c.id === activeCompany);
    if (!company) return null;

    if (activeSection === 'company-main') {
      return (
        <div className="bg-[#1a1a2e] rounded-3xl border border-[#2a2a4e] p-6">
          <div className="flex items-center gap-3 mb-6">
            <img src={company.logo} alt={company.name} className="h-10 w-auto" />
            <div>
              <h2 className="text-xl font-bold text-white">{company.name}</h2>
              <p className="text-sm text-[#808080]">{company.nameAr}</p>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {companySubSections.map((section) => (
              <Button
                key={section.id}
                onClick={() => setActiveSection(section.id)}
                className="h-28 bg-[#0a0a0a] hover:bg-[#2a2a4e] text-white border border-[#2a2a4e] hover:border-[#c9a227] flex flex-col items-center justify-center gap-2 transition-all rounded-2xl"
              >
                {section.icon}
                <span className="font-semibold">{section.name}</span>
              </Button>
            ))}
          </div>
        </div>
      );
    }

    const section = companySubSections.find(s => s.id === activeSection);
    return (
      <div className="bg-[#1a1a2e] rounded-3xl border border-[#2a2a4e] p-6">
        <div className="flex items-center gap-3 mb-6">
          <img src={company.logo} alt={company.name} className="h-8 w-auto" />
          <span className="text-[#808080]">/</span>
          {section?.icon}
          <h2 className="text-xl font-bold text-white">{section?.name}</h2>
        </div>

        <div className="text-center py-16 bg-[#0a0a0a] rounded-2xl border border-[#2a2a4e]">
          <DollarSign className="h-16 w-16 mx-auto text-[#c9a227]/30 mb-4" />
          <p className="text-[#808080] text-lg mb-2">لا توجد سجلات حالياً</p>
          <p className="text-[#606060] text-sm">سيتم عرض {section?.name} لـ {company.name} هنا</p>
        </div>
      </div>
    );
  };

  const renderOtherSectionContent = () => {
    // Invoice section
    if (activeSection === 'add-invoice') {
      return (
        <div className="bg-[#1a1a2e] rounded-3xl border border-[#2a2a4e] p-6">
          <div className="flex items-center gap-3 mb-6">
            <Receipt className="h-8 w-8 text-emerald-500" />
            <h2 className="text-xl font-bold text-white">إدراج فاتورة</h2>
            {pendingCount.invoices > 0 && (
              <span className="bg-amber-500/20 text-amber-400 px-3 py-1 rounded-full text-sm flex items-center gap-1">
                <Clock className="h-4 w-4" />
                {pendingCount.invoices} معلقة
              </span>
            )}
          </div>
          {loading ? (
            <div className="text-center py-16">
              <Loader2 className="h-8 w-8 mx-auto text-emerald-500 animate-spin" />
            </div>
          ) : (
            renderRecordsList(invoices, 'invoice')
          )}
        </div>
      );
    }

    // Contract section
    if (activeSection === 'add-contract') {
      return (
        <div className="bg-[#1a1a2e] rounded-3xl border border-[#2a2a4e] p-6">
          <div className="flex items-center gap-3 mb-6">
            <FileSignature className="h-8 w-8 text-blue-500" />
            <h2 className="text-xl font-bold text-white">إدراج عقد</h2>
            {pendingCount.contracts > 0 && (
              <span className="bg-amber-500/20 text-amber-400 px-3 py-1 rounded-full text-sm flex items-center gap-1">
                <Clock className="h-4 w-4" />
                {pendingCount.contracts} معلق
              </span>
            )}
          </div>
          {loading ? (
            <div className="text-center py-16">
              <Loader2 className="h-8 w-8 mx-auto text-blue-500 animate-spin" />
            </div>
          ) : (
            renderRecordsList(contracts, 'contract')
          )}
        </div>
      );
    }

    // Payment category archives
    const category = PAYMENT_CATEGORIES.find(c => c.id === activeSection);
    if (category) {
      return renderCategoryArchive();
    }

    return null;
  };

  const handleBack = () => {
    if (activeSection === 'add-invoice' || activeSection === 'add-contract') {
      setActiveSection('entry');
    } else if (activeSection === 'entry' || activeSection === 'review' || activeSection === 'financial-materials') {
      setActiveSection('main');
    } else if (activeSection === 'materials-subscriptions' || activeSection === 'materials-payments' || activeSection === 'materials-ads' || activeSection === 'materials-government') {
      setActiveSection('financial-materials');
    } else if (PAYMENT_CATEGORIES.find(c => c.id === activeSection)) {
      setActiveSection('review');
    } else if (activeSection === 'company-main') {
      setActiveCompany(null);
      setActiveSection('review');
    } else if (activeCompany && (activeSection === 'payments' || activeSection === 'subscriptions')) {
      setActiveSection('company-main');
    } else {
      navigate('/admin-dashboard');
    }
  };

  return (
    <div className="min-h-screen bg-[#0a0a0a] py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Breadcrumb */}
        <div className="mb-4">
          <Breadcrumb 
            pathAr="لوحة التحكم / المالية" 
            pathEn="Dashboard / Finance" 
          />
        </div>
        
        {/* Header */}
        <div className="flex items-center justify-between mb-8 animate-header">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-xl bg-[#1a1a2e] border border-[#2a2a4e] flex items-center justify-center">
              <TrendingUp className="h-6 w-6 text-[#c9a227]" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-white">المالية</h1>
              <p className="text-sm text-[#808080]">Finance</p>
            </div>
          </div>
          <Button
            onClick={handleBack}
            variant="outline"
            className="border-[#2a2a4e] text-white hover:bg-[#2a2a4e] rounded-xl btn-animate"
            data-testid="back-btn"
          >
            <ArrowRight className="h-4 w-4 me-2" />
            {activeSection !== 'main' ? 'رجوع' : 'العودة'}
          </Button>
        </div>

        {/* Content */}
        <div className="animate-fade-in">
        
        {/* Main View - Three Main Buttons */}
        {activeSection === 'main' && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 animate-stagger">
            {/* الإدراج */}
            <Button
              onClick={() => setActiveSection('entry')}
              className="h-40 bg-[#1a1a2e] hover:bg-[#2a2a4e] text-white border border-[#2a2a4e] hover:border-emerald-500 flex flex-col items-center justify-center gap-4 transition-all rounded-2xl btn-animate"
              data-testid="btn-entry"
            >
              <div className="w-16 h-16 rounded-2xl bg-emerald-500/20 flex items-center justify-center">
                <FileUp className="h-8 w-8 text-emerald-500" />
              </div>
              <span className="font-bold text-xl">الإدراج</span>
              <span className="text-sm text-[#808080]">إدراج الفواتير والعقود</span>
            </Button>

            {/* مراجعة السجلات المالية */}
            <Button
              onClick={() => setActiveSection('review')}
              className="h-40 bg-[#1a1a2e] hover:bg-[#2a2a4e] text-white border border-[#2a2a4e] hover:border-[#c9a227] flex flex-col items-center justify-center gap-4 transition-all rounded-2xl btn-animate"
              data-testid="btn-review"
            >
              <div className="w-16 h-16 rounded-2xl bg-[#c9a227]/20 flex items-center justify-center">
                <FolderOpen className="h-8 w-8 text-[#c9a227]" />
              </div>
              <span className="font-bold text-xl">مراجعة السجلات المالية</span>
              <span className="text-sm text-[#808080]">سجل الشركات والمدفوعات</span>
            </Button>
            
            {/* المواد المالية */}
            <Button
              onClick={() => setActiveSection('financial-materials')}
              className="h-40 bg-[#1a1a2e] hover:bg-[#2a2a4e] text-white border border-[#2a2a4e] hover:border-purple-500 flex flex-col items-center justify-center gap-4 transition-all rounded-2xl btn-animate"
              data-testid="btn-financial-materials"
            >
              <div className="w-16 h-16 rounded-2xl bg-purple-500/20 flex items-center justify-center">
                <Package className="h-8 w-8 text-purple-500" />
              </div>
              <span className="font-bold text-xl">المواد المالية</span>
              <span className="text-sm text-[#808080]">الاشتراكات والدفعات والإعلانات</span>
            </Button>
          </div>
        )}
        
        {/* Financial Materials Section */}
        {activeSection === 'financial-materials' && (
          <div className="space-y-6">
            <h2 className="text-xl font-bold text-white flex items-center gap-2">
              <Package className="h-6 w-6 text-purple-500" />
              المواد المالية
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {/* مواد الاشتراكات */}
              <Button
                onClick={() => setActiveSection('materials-subscriptions')}
                className="h-32 bg-[#1a1a2e] hover:bg-[#2a2a4e] text-white border border-[#2a2a4e] hover:border-blue-500 flex flex-col items-center justify-center gap-3 transition-all rounded-2xl"
                data-testid="btn-materials-subscriptions"
              >
                <CreditCard className="h-10 w-10 text-blue-500" />
                <span className="font-bold text-lg">مواد الاشتراكات</span>
              </Button>
              
              {/* مواد الدفعات */}
              <Button
                onClick={() => setActiveSection('materials-payments')}
                className="h-32 bg-[#1a1a2e] hover:bg-[#2a2a4e] text-white border border-[#2a2a4e] hover:border-emerald-500 flex flex-col items-center justify-center gap-3 transition-all rounded-2xl"
                data-testid="btn-materials-payments"
              >
                <DollarSign className="h-10 w-10 text-emerald-500" />
                <span className="font-bold text-lg">مواد الدفعات</span>
              </Button>
              
              {/* مواد مدفوعات الإعلانات */}
              <Button
                onClick={() => setActiveSection('materials-ads')}
                className="h-32 bg-[#1a1a2e] hover:bg-[#2a2a4e] text-white border border-[#2a2a4e] hover:border-orange-500 flex flex-col items-center justify-center gap-3 transition-all rounded-2xl"
                data-testid="btn-materials-ads"
              >
                <Megaphone className="h-10 w-10 text-orange-500" />
                <span className="font-bold text-lg">مواد مدفوعات الإعلانات</span>
              </Button>
              
              {/* مواد الخدمات الحكومية */}
              <Button
                onClick={() => setActiveSection('materials-government')}
                className="h-32 bg-[#1a1a2e] hover:bg-[#2a2a4e] text-white border border-[#2a2a4e] hover:border-amber-500 flex flex-col items-center justify-center gap-3 transition-all rounded-2xl"
                data-testid="btn-materials-government"
              >
                <Landmark className="h-10 w-10 text-amber-500" />
                <span className="font-bold text-lg">مواد الخدمات الحكومية</span>
              </Button>
            </div>
          </div>
        )}
        
        {/* Financial Materials Sub-sections */}
        {(activeSection === 'materials-subscriptions' || activeSection === 'materials-payments' || activeSection === 'materials-ads' || activeSection === 'materials-government') && (
          <div className="bg-[#1a1a2e] rounded-3xl border border-[#2a2a4e] p-6">
            <div className="flex items-center gap-3 mb-6">
              {activeSection === 'materials-subscriptions' && <CreditCard className="h-8 w-8 text-blue-500" />}
              {activeSection === 'materials-payments' && <DollarSign className="h-8 w-8 text-emerald-500" />}
              {activeSection === 'materials-ads' && <Megaphone className="h-8 w-8 text-orange-500" />}
              {activeSection === 'materials-government' && <Landmark className="h-8 w-8 text-amber-500" />}
              <h2 className="text-xl font-bold text-white">
                {activeSection === 'materials-subscriptions' && 'مواد الاشتراكات'}
                {activeSection === 'materials-payments' && 'مواد الدفعات'}
                {activeSection === 'materials-ads' && 'مواد مدفوعات الإعلانات'}
                {activeSection === 'materials-government' && 'مواد الخدمات الحكومية'}
              </h2>
            </div>
            
            <div className="text-center py-16 bg-[#0a0a0a] rounded-2xl border border-[#2a2a4e]">
              <Package className="h-16 w-16 mx-auto text-purple-500/30 mb-4" />
              <p className="text-[#808080] text-lg mb-2">لا توجد مواد مالية حالياً</p>
              <p className="text-[#606060] text-sm">سيتم عرض المواد المالية هنا عند إضافتها</p>
            </div>
          </div>
        )}

        {/* Entry Section */}
        {activeSection === 'entry' && (
          <div className="space-y-6">
            <h2 className="text-xl font-bold text-white flex items-center gap-2">
              <FileUp className="h-6 w-6 text-emerald-500" />
              الإدراج
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Button
                onClick={() => navigate('/dashboard/finance/records/new?type=invoice')}
                className="h-32 bg-[#1a1a2e] hover:bg-[#2a2a4e] text-white border border-[#2a2a4e] hover:border-emerald-500 flex flex-col items-center justify-center gap-3 transition-all rounded-2xl relative"
                data-testid="btn-add-invoice"
              >
                <Receipt className="h-10 w-10 text-emerald-500" />
                <span className="font-bold text-lg">إدراج فاتورة</span>
                {pendingCount.invoices > 0 && (
                  <span className="absolute top-3 left-3 bg-amber-500 text-white text-xs px-2 py-1 rounded-full">
                    {pendingCount.invoices} معلقة
                  </span>
                )}
              </Button>
              <Button
                onClick={() => navigate('/dashboard/finance/records/new?type=contract')}
                className="h-32 bg-[#1a1a2e] hover:bg-[#2a2a4e] text-white border border-[#2a2a4e] hover:border-blue-500 flex flex-col items-center justify-center gap-3 transition-all rounded-2xl relative"
                data-testid="btn-add-contract"
              >
                <FileSignature className="h-10 w-10 text-blue-500" />
                <span className="font-bold text-lg">إدراج عقد</span>
                {pendingCount.contracts > 0 && (
                  <span className="absolute top-3 left-3 bg-amber-500 text-white text-xs px-2 py-1 rounded-full">
                    {pendingCount.contracts} معلق
                  </span>
                )}
              </Button>
            </div>
          </div>
        )}

        {/* Review Section */}
        {activeSection === 'review' && (
          <div className="space-y-8">
            {/* سجل الشركات */}
            <div>
              <h2 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
                <Building2 className="h-5 w-5 text-[#c9a227]" />
                سجل الشركات
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {companies.map((company) => (
                  <Button
                    key={company.id}
                    onClick={() => {
                      setActiveCompany(company.id);
                      setActiveSection('company-main');
                    }}
                    className="h-32 bg-[#1a1a2e] hover:bg-[#2a2a4e] text-white border border-[#2a2a4e] hover:border-[#c9a227] flex flex-col items-center justify-center gap-3 transition-all rounded-2xl"
                    data-testid={`btn-company-${company.id}`}
                  >
                    <img src={company.logo} alt={company.name} className="h-12 w-auto" />
                    <div className="text-center">
                      <span className="font-semibold block">{company.name}</span>
                      <span className="text-xs text-[#808080]">{company.nameAr}</span>
                    </div>
                  </Button>
                ))}
              </div>
            </div>

            {/* المدفوعات */}
            <div>
              <h2 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
                <CreditCard className="h-5 w-5 text-[#c9a227]" />
                المدفوعات
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                {PAYMENT_CATEGORIES.map((category) => {
                  const CategoryIcon = category.icon;
                  return (
                    <Button
                      key={category.id}
                      onClick={() => setActiveSection(category.id)}
                      className="h-28 bg-[#1a1a2e] hover:bg-[#2a2a4e] text-white border border-[#2a2a4e] hover:border-[#c9a227] flex flex-col items-center justify-center gap-2 transition-all rounded-2xl"
                      data-testid={`btn-${category.id}`}
                    >
                      <CategoryIcon className="h-8 w-8 text-[#c9a227]" />
                      <span className="font-semibold text-sm text-center">{category.name}</span>
                    </Button>
                  );
                })}
              </div>
            </div>
          </div>
        )}

        {/* Invoice/Contract Section */}
        {(activeSection === 'add-invoice' || activeSection === 'add-contract') && renderOtherSectionContent()}

        {/* Category Archive */}
        {PAYMENT_CATEGORIES.find(c => c.id === activeSection) && renderOtherSectionContent()}

        {/* Company View */}
        {activeCompany && (activeSection === 'company-main' || activeSection === 'payments' || activeSection === 'subscriptions') && renderCompanyContent()}
        </div>
      </div>
    </div>
  );
}
